import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import GroupTagSelect from './GroupTagSelect';
import type { SelectOption } from './Select';
import LoadingSpinner from './LoadingSpinner';
import { addDaysYmd, formatDateEuropean } from '../utils/dateUtils';
import './GroupForm.css';
import './Giveaways.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;
const MENTION_GRACE_DAYS = 7;

type GroupOption = SelectOption<number>;

type GiveawayPayload = {
  id: number;
  name: string;
  startsOn: string;
  endsOn: string;
  groups: { id: number; name: string | null; level: string }[];
};

function groupLabel(group: { id: number; name?: string | null; level: string }) {
  return group.name?.trim() || `${group.level.toUpperCase()} Group`;
}

const GiveawayForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const { groups } = useAppSelector((state) => state.groups);

  const [name, setName] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [selected, setSelected] = useState<GroupOption[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    dispatch(fetchGroupsAsync({ page: 1, limit: 200, status: 'active' }));
  }, [dispatch]);

  useEffect(() => {
    if (!id || !token) return;
    axios.get(`${API_BASE_URL}/giveaways/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const giveaway = response.data.giveaway as GiveawayPayload;
      setName(giveaway.name);
      setStartsOn(giveaway.startsOn);
      setEndsOn(giveaway.endsOn);
      setSelected((giveaway.groups || []).map((group) => ({
        value: group.id,
        label: groupLabel(group),
      })));
    }).catch((err) => {
      setError(axios.isAxiosError(err) ? err.response?.data?.error || err.message : 'Failed to load giveaway');
    }).finally(() => setLoading(false));
  }, [id, token]);

  const activeOptions = useMemo<GroupOption[]>(
    () => groups.map((group) => ({ value: group.id, label: groupLabel(group) })),
    [groups],
  );

  const options = useMemo(() => {
    const byId = new Map(activeOptions.map((option) => [option.value, option]));
    for (const option of selected) {
      if (!byId.has(option.value)) byId.set(option.value, option);
    }
    return [...byId.values()];
  }, [activeOptions, selected]);

  const mentionUntil = endsOn ? addDaysYmd(endsOn, MENTION_GRACE_DAYS) : '';
  const allSelected = activeOptions.length > 0 && activeOptions.every((option) => selected.some((item) => item.value === option.value));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected((prev) => prev.filter((item) => !activeOptions.some((option) => option.value === item.value)));
      return;
    }
    const byId = new Map(selected.map((item) => [item.value, item]));
    for (const option of activeOptions) byId.set(option.value, option);
    setSelected([...byId.values()]);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!name.trim() || !startsOn || !endsOn) {
      setError('Name and period are required');
      return;
    }
    if (endsOn < startsOn) {
      setError('End date must be on or after the start date');
      return;
    }
    if (selected.length === 0) {
      setError('Select at least one group');
      return;
    }
    setSaving(true);
    const payload = {
      name: name.trim(),
      startsOn,
      endsOn,
      groupIds: selected.map((item) => item.value),
    };
    try {
      if (isEditing) {
        await axios.put(`${API_BASE_URL}/giveaways/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE_URL}/giveaways`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      navigate('/giveaways');
    } catch (err) {
      setError(axios.isAxiosError(err) ? err.response?.data?.error || err.message : 'Failed to save giveaway');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{isEditing ? 'Edit Giveaway' : 'New Giveaway'}</h1>
          <p>Choose the groups that can earn a mention reward during this experiment</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => navigate('/giveaways')}>Cancel</button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <form onSubmit={handleSubmit} className="consultation-edit">
          {error && <div className="error-message">{error}</div>}

          <section className="consultation-edit__card">
            <h2 className="consultation-edit__card-title">Campaign</h2>
            <div className="form-group">
              <label htmlFor="giveaway-name">Name *</label>
              <input
                id="giveaway-name"
                className="form-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. IG story — B1 Thursday"
              />
            </div>
            <div className="form-row-compact">
              <div className="form-group">
                <label htmlFor="giveaway-start">Starts *</label>
                <input
                  id="giveaway-start"
                  type="date"
                  className="form-input"
                  value={startsOn}
                  onChange={(event) => setStartsOn(event.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="giveaway-end">Ends *</label>
                <input
                  id="giveaway-end"
                  type="date"
                  className="form-input"
                  value={endsOn}
                  onChange={(event) => setEndsOn(event.target.value)}
                />
              </div>
            </div>
            <p className="giveaway-grace-hint">
              Mentions can still be recorded for 7 days after the period ends
              {mentionUntil ? ` — until ${formatDateEuropean(mentionUntil)}.` : '.'}
            </p>
          </section>

          <section className="consultation-edit__card">
            <h2 className="consultation-edit__card-title">Groups</h2>
            <div className="giveaway-groups-toolbar">
              <button type="button" className="btn-secondary" onClick={handleSelectAll} disabled={activeOptions.length === 0}>
                {allSelected ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <GroupTagSelect
              value={selected}
              onChange={setSelected}
              options={options}
              placeholder="Select active groups…"
            />
          </section>

          <div className="consultation-edit__actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/giveaways')} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-success" disabled={saving}>
              {saving ? 'Saving…' : isEditing ? 'Save giveaway' : 'Create giveaway'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default GiveawayForm;
